
<div class="totp-verify">
  <!-- Low backup codes warning after TOTP verification -->
  <div v-if="showLowCodesWarning && remainingCodes !== null" class="verification-state">
    <div class="text-center">
      <div class="icon-container">
        <div class="icon-wrapper">
          <LucideIcon name="ShieldAlert" :size="32" class="text-muted" />
        </div>
      </div>
      <h2 class="title">Verification Successful</h2>
      <p class="subtitle">Signing you in...</p>
      <div class="warning-box">
        <div class="warning-content">
          <LucideIcon name="AlertTriangle" :size="16" class="warning-icon" />
          <span class="warning-text">
            {{
              remainingCodes === 0
                ? 'No backup codes remaining! Reset 2FA in settings to generate new codes.'
                : `Only ${remainingCodes} backup code${remainingCodes === 1 ? '' : 's'} remaining`
            }}
          </span>
        </div>
      </div>
    </div>
    <div class="loading-container">
      <LucideIcon name="Loader2" :size="24" class="loading-spinner" />
    </div>
  </div>

  <!-- Success state - redirecting (no backup code warning) -->
  <div v-else-if="isRedirecting" class="verification-state">
    <div class="text-center">
      <div class="icon-container">
        <div class="icon-wrapper">
          <LucideIcon name="ShieldAlert" :size="32" class="text-muted" />
        </div>
      </div>
      <h2 class="title">Verification Successful</h2>
      <p class="subtitle">Signing you in...</p>
    </div>
    <div class="loading-container">
      <LucideIcon name="Loader2" :size="24" class="loading-spinner" />
    </div>
  </div>

  <!-- Success message after backup code verification -->
  <div v-else-if="remainingCodes !== null && mode === 'backup'" class="verification-state">
    <div class="text-center">
      <div class="icon-container">
        <div class="icon-wrapper">
          <LucideIcon name="KeyRound" :size="32" class="text-muted" />
        </div>
      </div>
      <h2 class="title">Backup Code Accepted</h2>
      <p class="subtitle">Signing you in...</p>
      <div v-if="remainingCodes <= 3" class="warning-box">
        <div class="warning-content">
          <LucideIcon name="AlertTriangle" :size="16" class="warning-icon" />
          <span class="warning-text">
            {{
              remainingCodes === 0
                ? 'No backup codes remaining!'
                : `Only ${remainingCodes} backup code${remainingCodes === 1 ? '' : 's'} remaining`
            }}
          </span>
        </div>
      </div>
    </div>
    <div class="loading-container">
      <LucideIcon name="Loader2" :size="24" class="loading-spinner" />
    </div>
  </div>

  <!-- Backup code entry mode -->
  <div v-else-if="mode === 'backup'" class="entry-mode">
    <!-- Header -->
    <div class="text-center">
      <div class="icon-container">
        <div class="icon-wrapper">
          <LucideIcon name="KeyRound" :size="32" class="text-muted" />
        </div>
      </div>
      <h2 class="title">Use Backup Code</h2>
      <p class="subtitle">Enter one of your single-use backup codes</p>
    </div>

    <!-- Backup Code Input -->
    <div class="input-section">
      <label for="backup-code" class="input-label">Backup Code</label>
      <input
        id="backup-code"
        ref="backupInput"
        type="text"
        placeholder="XXXX-XXXX"
        :value="backupCode"
        @input="handleBackupCodeChange"
        @keypress.enter="handleBackupCodeKeyPress"
        :disabled="isVerifying"
        class="backup-input"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="characters"
        spellcheck="false"
      />
      <p class="input-hint">Format: XXXX-XXXX (alphanumeric)</p>
      <div v-if="error" class="error-message">{{ error }}</div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <button
        type="button"
        class="btn btn-primary"
        @click="handleVerifyBackupCode"
        :disabled="!isValidBackupCodeFormat(backupCode) || isVerifying"
      >
        <LucideIcon v-if="isVerifying" name="Loader2" :size="16" class="btn-icon loading-spinner" />
        {{ isVerifying ? 'Verifying...' : 'Use Backup Code' }}
      </button>

      <!-- Switch to TOTP Link -->
      <div class="text-center">
        <button
          type="button"
          class="link-button"
          @click="switchToTOTPMode"
          :disabled="isVerifying"
        >
          Use authenticator app instead
        </button>
      </div>

      <!-- Back Button -->
      <button
        type="button"
        class="btn btn-ghost"
        @click="handleBack"
        :disabled="isVerifying"
      >
        <LucideIcon name="ArrowLeft" :size="16" class="btn-icon" />
        Back to login
      </button>
    </div>
  </div>

  <!-- TOTP code entry mode (default) -->
  <div v-else class="entry-mode">
    <!-- Header -->
    <div class="text-center">
      <div class="icon-container">
        <div class="icon-wrapper">
          <LucideIcon name="ShieldAlert" :size="32" class="text-muted" />
        </div>
      </div>
      <h2 class="title">Two-Factor Authentication</h2>
      <p class="subtitle">Enter the 6-digit code from your authenticator app</p>
    </div>

    <!-- 6-Digit Input Grid -->
    <div class="digit-section">
      <div class="digit-grid">
        <input
          v-for="(digit, index) in digits"
          :key="index"
          :ref="`digitInput${index}`"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="1"
          :value="digit"
          @input="handleDigitChange(index, $event)"
          @keydown="handleKeyDown(index, $event)"
          @paste="handlePaste(index, $event)"
          :disabled="isVerifying"
          :class="[
            'digit-input',
            { 'digit-error': error, 'digit-disabled': isVerifying }
          ]"
          :autocomplete="index === 0 ? 'one-time-code' : 'off'"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          :name="`otp-${index}`"
          :aria-label="`Digit ${index + 1} of 6`"
        />
      </div>

      <!-- Error Message -->
      <div v-if="error" class="error-message">{{ error }}</div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <button
        type="button"
        class="btn btn-primary"
        @click="handleVerifyClick"
        :disabled="!allDigitsEntered || isVerifying"
      >
        <LucideIcon v-if="isVerifying" name="Loader2" :size="16" class="btn-icon loading-spinner" />
        {{ isVerifying ? 'Verifying...' : 'Verify Code' }}
      </button>

      <!-- Backup Code Link -->
      <div class="text-center">
        <button
          type="button"
          class="link-button"
          @click="switchToBackupMode"
          :disabled="isVerifying"
        >
          Use a backup code instead
        </button>
      </div>

      <!-- Back Button -->
      <button
        type="button"
        class="btn btn-ghost"
        @click="handleBack"
        :disabled="isVerifying"
      >
        <LucideIcon name="ArrowLeft" :size="16" class="btn-icon" />
        Back to login
      </button>
    </div>
  </div>
</div>
